import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  TextField,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { FormEvent, useEffect, useRef, useState } from 'react';
import {
  YANDEX_SEARCH_MODES,
  YANDEX_SEARCH_MODE_LABELS,
  type YandexSearchMode,
  SEARCH_PROVIDERS,
  SEARCH_PROVIDER_LABELS,
} from '../../../api/src/search/search-provider.types';
import type { SearchProvider } from '../api/types';
import { api } from '../api/client';
import type { SearchKeywordInput, SearchRegion, SearchEngine } from '../api/types';
import { Box, Stack, Typography } from './mui-system';

interface SearchKeywordDialogProps {
  busy: boolean;
  mode?: 'keyword' | 'profile';
  providers?: Record<SearchProvider, boolean>;
  initialConfig?: Omit<SearchKeywordInput, 'query'>;
  initialSyncFallbackOn429?: boolean;
  error?: string;
  open: boolean;
  workspaceId: string;
  onClose: () => void;
  onSubmit: (input: SearchKeywordInput & { yandexSyncFallbackOn429?: boolean }) => Promise<void>;
}

const defaultYandexRegion: SearchRegion = { code: '213', name: 'Москва' };

export function SearchKeywordDialog({
  busy,
  mode = 'keyword',
  initialConfig,
  initialSyncFallbackOn429 = false,
  providers,
  error,
  open,
  workspaceId,
  onClose,
  onSubmit,
}: SearchKeywordDialogProps) {
  const [provider, setProvider] = useState<SearchProvider>(initialConfig?.provider ?? 'xmlstock');
  const [yandexSearchMode, setYandexSearchMode] = useState<YandexSearchMode>(
    initialConfig?.yandexSearchMode ?? 'deferred',
  );
  const [fallback, setFallback] = useState(initialSyncFallbackOn429);
  const [searchEngine, setSearchEngine] = useState<SearchEngine>(
    initialConfig?.searchEngine ?? 'yandex',
  );
  const initialRegion = initialConfig
    ? { code: initialConfig.regionCode, name: initialConfig.regionName }
    : defaultYandexRegion;
  const [yandexRegion, setYandexRegion] = useState<SearchRegion | null>(initialRegion);
  const [regionOptions, setRegionOptions] = useState<SearchRegion[]>([initialRegion]);
  const [regionInput, setRegionInput] = useState(regionLabel(initialRegion));
  const [regionQuery, setRegionQuery] = useState('');
  const [regionLoading, setRegionLoading] = useState(false);
  const [regionLookupError, setRegionLookupError] = useState('');
  const [regionSelectionError, setRegionSelectionError] = useState('');
  const [manualRegion, setManualRegion] = useState(false);
  const [manualRegionName, setManualRegionName] = useState(initialRegion.name);
  const [manualRegionCode, setManualRegionCode] = useState(initialRegion.code);
  const regionRequest = useRef(0);

  useEffect(() => {
    if (!open || searchEngine !== 'yandex' || manualRegion) {
      regionRequest.current += 1;
      setRegionLoading(false);
      return undefined;
    }
    const request = ++regionRequest.current;
    const timer = window.setTimeout(
      () => {
        setRegionLoading(true);
        setRegionLookupError('');
        void api
          .searchYandexRegions(workspaceId, regionQuery)
          .then((response) => {
            if (request === regionRequest.current) setRegionOptions(response.items);
          })
          .catch((error: unknown) => {
            if (request === regionRequest.current) {
              setRegionLookupError(
                error instanceof Error
                  ? error.message
                  : 'Не удалось загрузить справочник регионов Яндекса',
              );
            }
          })
          .finally(() => {
            if (request === regionRequest.current) setRegionLoading(false);
          });
      },
      regionQuery ? 250 : 0,
    );
    return () => {
      window.clearTimeout(timer);
      if (request === regionRequest.current) regionRequest.current += 1;
    };
  }, [manualRegion, open, regionQuery, searchEngine, workspaceId]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const regionCode = String(form.get('regionCode')).trim();
    const regionName = String(form.get('regionName')).trim();
    if (!regionCode || regionName.length < 2) {
      setRegionSelectionError(
        searchEngine === 'yandex' && !manualRegion
          ? 'Выберите регион из справочника или включите ручной ввод.'
          : 'Укажите название и код региона.',
      );
      return;
    }
    await onSubmit({
      query: mode === 'profile' ? '' : String(form.get('query')),
      provider,
      yandexSearchMode: provider === 'yandex_search_api' ? yandexSearchMode : 'deferred',
      ...(mode === 'profile'
        ? {
            yandexSyncFallbackOn429:
              provider === 'yandex_search_api' && yandexSearchMode === 'deferred' && fallback,
          }
        : {}),
      searchEngine,
      regionCode,
      regionName,
      device: String(form.get('device')) as 'desktop' | 'mobile',
      depth: Number(form.get('depth')),
    });
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <Box component="form" onSubmit={submit}>
        <DialogTitle>
          {mode === 'profile' ? 'Профиль сбора группы' : 'Новый ключевой запрос'}
        </DialogTitle>
        <DialogContent>
          <Stack gap={2} pt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            {mode === 'keyword' && (
              <TextField
                name="query"
                label="Ключевой запрос"
                required
                autoFocus
                slotProps={{ htmlInput: { maxLength: 300 } }}
                helperText="Повтор запроса с теми же настройками откроет сохранённый срез без нового платного обращения к провайдеру."
              />
            )}
            <TextField
              select
              label="Провайдер выдачи"
              value={provider}
              onChange={(event) => {
                const next = event.target.value as SearchProvider;
                setProvider(next);
                if (next === 'yandex_search_api') setSearchEngine('yandex');
              }}
              helperText={
                providers && !providers[provider]
                  ? 'Провайдер не настроен на сервере. Профиль можно сохранить, сбор будет доступен после настройки.'
                  : 'Провайдер сохраняется в профиле и истории сбора.'
              }
            >
              {SEARCH_PROVIDERS.map((value) => (
                <MenuItem key={value} value={value}>
                  {SEARCH_PROVIDER_LABELS[value]}
                </MenuItem>
              ))}
            </TextField>
            {provider === 'yandex_search_api' && (
              <TextField
                select
                label="Режим Yandex Search API"
                value={yandexSearchMode}
                onChange={(event) => setYandexSearchMode(event.target.value as YandexSearchMode)}
                helperText={
                  yandexSearchMode === 'sync'
                    ? 'Результат приходит сразу. Синхронные запросы значительно дороже отложенных; режим применяется и к ежедневному сбору.'
                    : 'Дешевле. Ожидание от 5 минут до нескольких часов.'
                }
              >
                {YANDEX_SEARCH_MODES.map((value) => (
                  <MenuItem key={value} value={value}>
                    {YANDEX_SEARCH_MODE_LABELS[value]}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {mode === 'profile' &&
              provider === 'yandex_search_api' &&
              yandexSearchMode === 'deferred' && (
                <Stack>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={fallback}
                        onChange={(_event, checked) => setFallback(checked)}
                      />
                    }
                    label="Резервный синхронный сбор при HTTP 429"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Если отложенный запрос отклонён с HTTP 429, выполнить одну синхронную попытку.
                    Она значительно дороже. Действует и на ежедневный сбор; история остаётся
                    прежней.
                  </Typography>
                </Stack>
              )}
            <Box
              display="grid"
              gap={1.5}
              sx={{ gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}
            >
              <TextField
                select
                name="searchEngine"
                label="Поисковая система"
                value={searchEngine}
                onChange={(event) => {
                  setSearchEngine(event.target.value as SearchEngine);
                  setRegionSelectionError('');
                }}
                helperText={
                  provider === 'yandex_search_api'
                    ? 'Yandex Search API поддерживает только Яндекс.'
                    : 'XMLStock поддерживает Яндекс и Google.'
                }
              >
                <MenuItem value="yandex">Яндекс</MenuItem>
                <MenuItem value="google" disabled={provider === 'yandex_search_api'}>
                  Google
                </MenuItem>
              </TextField>
              <TextField
                select
                name="device"
                label="Устройство"
                defaultValue={initialConfig?.device ?? 'desktop'}
              >
                <MenuItem value="desktop">Desktop</MenuItem>
                <MenuItem value="mobile">Mobile</MenuItem>
              </TextField>
              {searchEngine === 'yandex' && !manualRegion ? (
                <Stack gap={0.5} sx={{ gridColumn: { sm: 'span 2' } }}>
                  <input type="hidden" name="regionName" value={yandexRegion?.name ?? ''} />
                  <input type="hidden" name="regionCode" value={yandexRegion?.code ?? ''} />
                  <Autocomplete
                    value={yandexRegion}
                    inputValue={regionInput}
                    options={regionOptions}
                    loading={regionLoading}
                    filterOptions={(options) => options}
                    getOptionLabel={regionLabel}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    noOptionsText="Регион не найден"
                    loadingText="Ищем регионы…"
                    onInputChange={(_event, value, reason) => {
                      if (reason !== 'input') return;
                      setRegionInput(value);
                      setRegionQuery(value);
                      setYandexRegion(null);
                      setRegionSelectionError('');
                    }}
                    onChange={(_event, value) => {
                      if (value) {
                        setYandexRegion(value);
                        setRegionInput(regionLabel(value));
                        setRegionQuery('');
                        setManualRegionName(value.name);
                        setManualRegionCode(value.code);
                      } else {
                        setYandexRegion(null);
                        setRegionInput('');
                        setRegionQuery('');
                      }
                      setRegionSelectionError('');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Регион Яндекса"
                        required
                        error={Boolean(regionSelectionError)}
                        helperText={
                          regionSelectionError ||
                          regionLookupError ||
                          'Начните вводить страну, область, город или код lr.'
                        }
                      />
                    )}
                  />
                  <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <Button
                      type="button"
                      size="small"
                      onClick={() => {
                        setManualRegion(true);
                        setRegionSelectionError('');
                      }}
                    >
                      Указать lr вручную
                    </Button>
                    <Typography color="text.secondary" fontSize={12}>
                      <Link
                        href="https://yandex.cloud/ru/docs/search-api/reference/regions"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Частые регионы
                      </Link>{' '}
                      ·{' '}
                      <Link
                        href="https://xmlstock.com/geotargets-yandex.csv"
                        target="_blank"
                        rel="noreferrer"
                      >
                        полный CSV
                      </Link>
                    </Typography>
                  </Stack>
                </Stack>
              ) : (
                <>
                  <TextField
                    name="regionName"
                    label="Регион"
                    value={manualRegionName}
                    onChange={(event) => {
                      setManualRegionName(event.target.value);
                      setRegionSelectionError('');
                    }}
                    required
                    slotProps={{ htmlInput: { maxLength: 80 } }}
                  />
                  <TextField
                    name="regionCode"
                    label="Код региона (lr)"
                    value={manualRegionCode}
                    onChange={(event) => {
                      setManualRegionCode(event.target.value);
                      setRegionSelectionError('');
                    }}
                    required
                    error={Boolean(regionSelectionError)}
                    slotProps={{ htmlInput: { maxLength: 20 } }}
                    helperText={
                      regionSelectionError || 'Идентификатор региона выбранной поисковой системы.'
                    }
                  />
                  {searchEngine === 'yandex' && (
                    <Button
                      type="button"
                      size="small"
                      sx={{ alignSelf: 'flex-start' }}
                      onClick={() => {
                        setManualRegion(false);
                        setRegionInput(regionLabel(yandexRegion ?? defaultYandexRegion));
                        setRegionQuery('');
                        setRegionSelectionError('');
                      }}
                    >
                      Выбрать из справочника
                    </Button>
                  )}
                </>
              )}
              <TextField
                select
                name="depth"
                label="Глубина выдачи"
                defaultValue={initialConfig?.depth ?? (mode === 'profile' ? 20 : 100)}
                helperText="Для Google каждые 10 позиций требуют отдельной страницы XMLStock."
                sx={{ gridColumn: { sm: 'span 2' } }}
              >
                {[10, 20, 30, 50, 100].map((depth) => (
                  <MenuItem key={depth} value={depth}>
                    Top-{depth}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Alert severity="info">
              {mode === 'profile'
                ? 'Параметры применяются к запросам выбранной группы. Изменение параметров поиска создаёт отдельную историю, переключение резерва сохраняет прежнюю; сбор запускается по кнопке «Обновить выдачу».'
                : 'Настройки фиксируются в запросе и во всех его неизменяемых срезах. Другая конфигурация того же текста создаст отдельный ключевой запрос.'}
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Отмена</Button>
          <Button type="submit" variant="contained" disabled={busy}>
            {mode === 'profile' ? 'Применить к группе' : 'Добавить и получить Top'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function regionLabel(region: SearchRegion): string {
  return `${region.code} — ${region.name}`;
}
